import { inject } from 'inversify';
import { Injectable } from '../../decorators/injectable';
import { HttpService, type IHttpService } from '../http/http.service';
import {
  AiExpirySuggestionLimitError,
  type IGoodsReceivingExtractionService,
  type SuggestExpiryDateInput,
  type SuggestExpiryDateResponse,
} from './goods-receiving.types';

@Injectable()
class GoodsReceivingExtractionService implements IGoodsReceivingExtractionService {
  constructor(@inject(HttpService) private readonly httpService: IHttpService) {}

  async extractItemData(file: File): Promise<{
    productName: string | null;
    quantity: string | null;
    unit: string | null;
    batchNumber: string | null;
    expiryDate: string | null;
    allergens: Array<{ allergenCode: string; category: 'CONTAINS' | 'MAY_CONTAIN' }> | null;
  } | null> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.httpService.postForm<{
      productName: string | null;
      quantity: string | null;
      unit: string | null;
      batchNumber: string | null;
      expiryDate: string | null;
      allergens: Array<{ allergenCode: string; category: 'CONTAINS' | 'MAY_CONTAIN' }> | null;
      isValid: boolean;
    }>('delivery/extract-item-data', formData);

    if (!response.isValid) {
      return null;
    }

    return {
      productName: response.productName,
      quantity: response.quantity,
      unit: response.unit,
      batchNumber: response.batchNumber,
      expiryDate: response.expiryDate,
      allergens: response.allergens ?? null,
    };
  }

  async suggestExpiryDate(input: SuggestExpiryDateInput): Promise<SuggestExpiryDateResponse> {
    try {
      return await this.httpService.post<SuggestExpiryDateResponse>(
        'delivery/suggest-expiry-date',
        input,
      );
    } catch (error: unknown) {
      // The HTTP layer surfaces 429 as a status code on the thrown error. We
      // narrow to a typed domain error so the UI can render the "weekly limit
      // reached" copy without parsing HTTP plumbing.
      if (isHttpStatus(error, 429)) {
        throw new AiExpirySuggestionLimitError();
      }
      throw error;
    }
  }
}

function isHttpStatus(error: unknown, status: number): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const candidate = error as { response?: { status?: number } };
  return candidate.response?.status === status;
}

export { GoodsReceivingExtractionService };
